/**
 * Application Service
 * Handles all operations related to job applications, including details retrieval and matching data
 */

import { supabase } from '../utils/supabase';

const applicationService = {
  /**
   * Fetch detailed application information including match breakdown and rate data
   * @param {string} applicationId - The application ID to fetch details for
   * @returns {Promise<{success: boolean, data?: {matchBreakdown: object, proposedRate: number, proposedRateMin: number, proposedRateMax: number, availabilityStart: string, portfolioLinks: array}, error?: string}>}
   */
  getApplicationDetails: async (applicationId) => {
    try {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      const { data, error } = await supabase
        .from('job_applications')
        .select('match_percentage, match_breakdown, match_factors, proposed_rate, proposed_rate_min, proposed_rate_max, availability_start, portfolio_links')
        .eq('id', applicationId)
        .single();

      if (error) {
        // Check if it's a "not found" error vs other errors
        if (error.code === 'PGRST116') {
          // Row not found - return empty data structure
          return {
            success: true,
            data: {
              matchBreakdown: null,
              proposedRate: null,
              proposedRateMin: null,
              proposedRateMax: null,
              availabilityStart: null,
              portfolioLinks: []
            }
          };
        }
        throw error;
      }

      // Normalize and sanitize the response
      const normalizedData = {
        matchBreakdown: (data?.match_breakdown && typeof data.match_breakdown === 'object') ? data.match_breakdown : null,
        proposedRate: data?.proposed_rate || null,
        proposedRateMin: data?.proposed_rate_min || null,
        proposedRateMax: data?.proposed_rate_max || null,
        availabilityStart: data?.availability_start || null,
        portfolioLinks: (Array.isArray(data?.portfolio_links) ? data.portfolio_links : [])
      };

      return {
        success: true,
        data: normalizedData
      };

    } catch (error) {
      console.error(`Error fetching application details for ${applicationId}:`, error);
      return {
        success: false,
        error: error.message || 'Failed to fetch application details'
      };
    }
  }
};

export default applicationService;
